'use client';
import { faMessageLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { useState } from 'react';
import { CSVLink } from 'react-csv';

import { csvHeaders } from '@/modules/league/new-components/constants';
import {
  useGetFlexNotificationAndResendGroupSplitEmails,
  useGetFlexNotificationAndResendOptInReminder,
  useSaveStatsReseed,
} from '@/modules/league/new-components/rsvp-components/hooks';
import SendMessageModal from '@/modules/league/new-components/rsvp-components/SendMessageModal';
import { GameDayInfo, Player } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { formatDateWithTimezone } from '@/utils/actions/leagues/gameDay/functions/getHeaderGameTimeBox';
import { toast } from 'react-toastify';

interface RSVPLinksProps {
  headerContent: GameDayInfo;
  playingPlayers: Player[];
  waitListPlayers: Player[];
  notPlayingPlayers: Player[];
  params: { slug: string; sessionId: string; matchDateId: string };
  isAuthorized: boolean;
}

const RSVPSLinks = ({
  headerContent,
  playingPlayers,
  waitListPlayers,
  notPlayingPlayers,
  params,
  isAuthorized,
}: RSVPLinksProps) => {
  const queryClient = useQueryClient();
  const { details } = headerContent;
  const { slug, sessionId, matchDateId } = params;
  const { setLoading } = useLoader();

  const { mutate: saveStatsReseed } = useSaveStatsReseed();
  const resendGroupSplitEmails = useGetFlexNotificationAndResendGroupSplitEmails(
    details.leagueTitle,
    sessionId,
    matchDateId,
    slug
  );
  const optInReminder = useGetFlexNotificationAndResendOptInReminder(
    details.leagueTitle,
    sessionId,
    matchDateId,
    slug
  );

  const [show, setShow] = useState(false);

  const showSeedByStanding =
    !details.DateLockScores &&
    !details.isMatchStarted &&
    playingPlayers &&
    playingPlayers.length > 0;
  const showExportCsv = csvHeaders && playingPlayers && details.isLocked && !details?.isFlexLeague;
  const timezoneAbbreviationForGameDay = formatDateWithTimezone(
    details?.FromDateTime,
    details?.Timezone_IanaID
  );
  const gameDayTitle = `${moment.parseZone(details?.FromDateTime).format('ddd MMM DD, hh:mm A')} ${timezoneAbbreviationForGameDay}`;

  const buttonClasses =
    'rounded-md border 5rt3 bg-white !px-3 !py-2 font-semibold !text-sm tracking-wider !text-leagues-300 hover:!text-gray-900 border-gray-300 shadow-xs';

  return (
    <>
      {isAuthorized ? (
        <div
          className={`mx-auto flex h-auto w-full flex-col gap-2 px-2 md:flex md:flex-row md:px-3 ${showSeedByStanding ? 'justify-left md:px-3' : 'justify-left md:px-1'} ${showExportCsv ? 'justify-between md:px-3' : ''}`}
        >
          {showExportCsv || showSeedByStanding ? (
            <div className='flex justify-between gap-2 px-2 md:justify-start md:px-1'>
              {showExportCsv && (
                <Button variant='link-gray' className={buttonClasses}>
                  <CSVLink
                    headers={csvHeaders}
                    filename={`${details?.leagueTitle} ${details?.Title} OptIns.csv`}
                    data={playingPlayers}
                  >
                    Export Playing CSV
                  </CSVLink>
                </Button>
              )}
              {showSeedByStanding ? (
                <Button
                  variant='link-gray'
                  className={buttonClasses}
                  onClick={() => {
                    setLoading(true);
                    saveStatsReseed(
                      { matchDateId: details.MatchDateID },
                      {
                        onSuccess: (data) => {
                          if (data.message) {
                            toast.success(data.message);
                          }
                          queryClient.invalidateQueries(['league_rsvp']);
                          setLoading(false);
                        },
                        onError: () => {
                          toast.error('Something went wrong. Try again.');
                          setLoading(false);
                        },
                      }
                    );
                  }}
                >
                  Seed by standing
                </Button>
              ) : null}
            </div>
          ) : null}
          {details?.DateLockScores === null && details && details.showMessageToEveryone && (
            <Button
              variant='link-gray'
              className={`${buttonClasses} md:justify-left justify-center`}
              onClick={() => setShow(true)}
            >
              <FontAwesomeIcon
                icon={faMessageLines}
                className='mr-2 text-leagues-300/10'
                size='lg'
              />
              Send message
            </Button>
          )}
          {details.isFlexLeague &&
            details?.DateLockScores === null &&
            details &&
            details?.TotalTeamsYes > 0 &&
            details.isMatchStarted && (
              <div className='flex flex-col gap-2 px-2 md:flex-row md:px-1'>
                <Button
                  variant='link-gray'
                  className={`${buttonClasses} max-w-[230px]`}
                  onClick={() => {
                    resendGroupSplitEmails.mutate();
                  }}
                >
                  Reesend Group Split Emails
                </Button>
                <Button
                  variant='link-gray'
                  className={`${buttonClasses} max-w-[205px]`}
                  onClick={() => {
                    optInReminder.mutate();
                  }}
                >
                  Resend Opt In Reminder
                </Button>
              </div>
            )}
        </div>
      ) : null}

      {show && (
        <SendMessageModal
          openModal={show}
          setIsModalOpen={setShow}
          title={`Send message to`}
          isAuthorized={isAuthorized}
          isWaitlist={details.isWaitlist}
          playingPlayers={playingPlayers}
          waitListPlayers={waitListPlayers}
          notPlayingPlayers={notPlayingPlayers}
          sendMessageToMultiplePlayers={true}
          leagueId={details.LeagueID}
          slug={slug}
          sessionId={sessionId}
          matchDateId={matchDateId}
          leagueTitle={details.leagueTitle}
          gameDayTitle={gameDayTitle}
        />
      )}
    </>
  );
};

export default RSVPSLinks;
